/**
 * Tenant management API methods.
 */

import apiClient from './client';
import type { Tenant, TenantCreateRequest, TenantUpdateRequest } from '@/types/tenant';

/** List all tenants (platform admin only). */
export async function getTenants(): Promise<Tenant[]> {
  const { data } = await apiClient.get<Tenant[]>('/v1/tenants');
  return data;
}

/** Provision a new tenant with an initial admin user. */
export async function createTenant(payload: TenantCreateRequest): Promise<Tenant> {
  const { data } = await apiClient.post<Tenant>('/v1/tenants', payload);
  return data;
}

/** Get a single tenant by ID. */
export async function getTenant(tenantId: string): Promise<Tenant> {
  const { data } = await apiClient.get<Tenant>(`/v1/tenants/${tenantId}`);
  return data;
}

/** Update a tenant's details (platform admin only). */
export async function updateTenant(tenantId: string, payload: TenantUpdateRequest): Promise<Tenant> {
  const { data } = await apiClient.put<Tenant>(`/v1/tenants/${tenantId}`, payload);
  return data;
}

/** Deactivate a tenant (platform admin only). */
export async function deactivateTenant(tenantId: string): Promise<Tenant> {
  const { data } = await apiClient.post<Tenant>(`/admin/platform/tenants/${tenantId}/deactivate`);
  return data;
}

/** Reactivate a previously deactivated tenant (platform admin only). */
export async function reactivateTenant(tenantId: string): Promise<Tenant> {
  const { data } = await apiClient.post<Tenant>(`/admin/platform/tenants/${tenantId}/reactivate`);
  return data;
}

/** Permanently delete a tenant and all its data (platform admin only). */
export async function deleteTenant(tenantId: string): Promise<void> {
  await apiClient.delete(`/admin/platform/tenants/${tenantId}`);
}
